import { CloseOutlined, DislikeOutlined, DownOutlined, LikeOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Divider, Flex, Select, Tag, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useEndSession,
  usePusherSubscription,
  useSendMessage,
  useSession,
  useSessionFeedback,
  useSessionSurveyIds,
  useSubmitFeedback,
  useUpdateSurveyIds,
} from '../../../api/Numina';
import { useSurveys } from '../../../api/Surveys';
import Loading from '../../../components/Loading';
import { QueryKeys, SurveyUploadType } from '../../../constants';
import useParams from '../../../hooks/useParams';
import { Gray6 } from '../../../styles';
import { queryType } from '../../../types';
import MessageInput from './MessageInput';
import MessageItem from './MessageItem';
import WelcomePage from './WelcomePage';
import MarkdownWithAssetLinks from './components/MarkdownWithAssetLinks';

const { Text } = Typography;

const StyledFlex = styled(Flex)`
  padding: 0 10%;
  height: 100%;
  position: relative;
`;

const StyledMessageFlex = styled(Flex)`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  padding: 12px 24px 300px 0;
`;

const StyledDivider = styled(Divider)`
  margin: 4px 0 0;
  margin-inline: -20%;
  width: calc(100% + 40%);
  border-color: ${Gray6};
  opacity: 0.3;
`;

const BottomSection = styled(Flex)`
  position: sticky;
  bottom: -10px;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  margin-top: auto;
  background: white;
  padding-top: 12px;
  z-index: 10;
`;

const ButtonContainer = styled(Flex)`
  position: sticky;
  bottom: 90px;
  margin-top: auto;
  z-index: 10;
`;

const DisclaimerText = styled(Text)`
  font-size: 12px;
`;

const SectionLabel = styled(Text)`
  font-weight: 500;
`;

const ContextColumn = styled(Flex)`
  &:first-child {
    flex-shrink: 0;
    max-width: 50%;
  }

  &:last-child {
    flex: 1;
  }
`;

const StyledTag = styled(Tag)`
  margin-bottom: 8px;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 22px;
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
`;

const MessageCard = styled(Card)`
  background: #e6f4ff;
  border: 1px solid #91caff;
  max-width: 75%;
  p {
    margin: 0;
    word-wrap: break-word;
  }
`;

const FeedbackRow = styled(Flex)`
  opacity: 0.5;
  transition: opacity 0.2s;
  &:hover {
    opacity: 1;
  }
`;

function FeedbackButtons({ messageIndex, surveyId, initialRating = null }) {
  const { mutate: submitFeedback, isPending: isSubmitting } = useSubmitFeedback(surveyId);
  const [selected, setSelected] = useState(initialRating ?? null);

  const handleClick = (rating) => {
    if (rating === selected || isSubmitting) return;
    setSelected(rating);
    submitFeedback({ messageIndex, rating });
  };

  return (
    <FeedbackRow gap={4}>
      <Tooltip title="Good response">
        <Button
          type={selected === 'UP' ? 'primary' : 'text'}
          size="small"
          icon={<LikeOutlined />}
          data-cy="feedback-up"
          onClick={() => handleClick('UP')}
        />
      </Tooltip>
      <Tooltip title="Bad response">
        <Button
          type={selected === 'DOWN' ? 'primary' : 'text'}
          size="small"
          danger={selected === 'DOWN'}
          icon={<DislikeOutlined />}
          data-cy="feedback-down"
          onClick={() => handleClick('DOWN')}
        />
      </Tooltip>
    </FeedbackRow>
  );
}

FeedbackButtons.propTypes = {
  messageIndex: PropTypes.number.isRequired,
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialRating: PropTypes.string,
};

function ChatTab({ survey }) {
  const { surveyId } = useParams();
  const [inputValue, setInputValue] = useState('');
  const [pusherChannel, setPusherChannel] = useState(null);
  const [isUserAtBottom, setUserAtBottom] = useState(true);
  const [messageContainerRef, setMessageContainerRef] = useState(null);
  const queryClient = useQueryClient();

  const { mutate: updateSurveyIds, isLoading: updateSurveyIdsLoading } =
    useUpdateSurveyIds(surveyId);
  const { mutate: endSession, isLoading: endingSession } = useEndSession(surveyId);
  const { mutate: sendMessage, isLoading: sendMessageLoading } = useSendMessage(surveyId, {
    onSuccess: (response) => {
      // Set Pusher channel from response
      if (response.data.channel_name) {
        setPusherChannel(response.data.channel_name);
      }
    },
  });

  const { data: sessionFeedback } = useSessionFeedback(surveyId);

  const { data: session, isLoading: sessionLoading } = useSession(surveyId, {
    onSuccess: (data) => {
      // Extract Pusher channel from session state if available
      // This handles page refreshes where we need to reconnect to the channel
      if (data.data && data.data.length > 0) {
        // Session history endpoint doesn't return channel,
        // but we'll get it from send_message response
      }
    },
  });
  const { data: sessionSurveyIds, isLoading: getSurveyIdsLoading } = useSessionSurveyIds(surveyId, {
    onSuccess: (data) => {
      if (data.data && data.data.length === 0) {
        updateSurveyIds({ data: { survey_ids: [surveyId] } });
      }
    },
  });

  // Only call surveys API after session has loaded
  const { data: surveys, isLoading: surveysLoading } = useSurveys({
    enabled: !sessionLoading && !!session,
    select: (data) => {
      const filteredData = data.data.filter((s) => !s.is_demo && !s.is_archived);
      return {
        ...data,
        data: filteredData,
      };
    },
  });

  // Get additional survey IDs (excluding the current survey)
  const additionalSurveyIds = useMemo(() => {
    if (!sessionSurveyIds?.data || !surveyId) return [];
    return sessionSurveyIds.data.filter((id) => id !== parseInt(surveyId, 10));
  }, [sessionSurveyIds, surveyId]);

  // Subscribe to Pusher channel for real-time updates
  usePusherSubscription(surveyId, pusherChannel);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (messageContainerRef) {
      messageContainerRef.scrollTop = messageContainerRef.scrollHeight;
    }
  };

  const scrollToLatestMessage = () => {
    if (messageContainerRef) {
      const container = messageContainerRef;
      container.scrollTop = container.scrollHeight - container.clientHeight - 200;
    }
  };

  useEffect(() => {
    if (!messageContainerRef) return;

    const checkAtBottom = () => {
      const atBottom =
        Math.abs(
          messageContainerRef.scrollHeight -
            messageContainerRef.scrollTop -
            messageContainerRef.clientHeight,
        ) < 250;
      setUserAtBottom(atBottom);
    };

    // Listen for user scroll
    messageContainerRef.addEventListener('scroll', checkAtBottom);

    // Initial check
    checkAtBottom();

    // eslint-disable-next-line consistent-return
    return () => {
      messageContainerRef.removeEventListener('scroll', checkAtBottom);
    };
  }, [messageContainerRef]);

  const handleUpdateSurveyIds = (selectedAdditionalIds) => {
    // Combine current survey ID with selected additional survey IDs
    const allSurveyIds = [parseInt(surveyId, 10), ...selectedAdditionalIds];
    updateSurveyIds({ data: { survey_ids: allSurveyIds } });
  };

  const handleEndSession = () => {
    endSession(
      { data: {} },
      {
        onSuccess: () => {
          // Clear the chat history in the query cache
          queryClient.setQueryData([QueryKeys.SESSION, surveyId], (prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              data: [],
            };
          });
          // Invalidate to refetch fresh session
          queryClient.invalidateQueries([QueryKeys.SESSION, surveyId]);
          // Clear additional surveys selection - reset to only current survey
          updateSurveyIds({ data: { survey_ids: [parseInt(surveyId, 10)] } });
        },
      },
    );
  };

  if (surveysLoading || sessionLoading) {
    return <Loading />;
  }

  const hasMessages = session?.data && Array.isArray(session.data) && session.data.length > 0;

  // Check if a message is currently being processed by looking for loading placeholder
  const isProcessingMessage = session?.data?.some((item) => item.loading) || false;

  // Group items: combine user message with all following agent items until next user message
  const groupedMessages = [];
  if (hasMessages) {
    let currentGroup = null;

    session.data.forEach((item) => {
      if (item.role === 'user') {
        // Start a new group with the user message
        if (currentGroup) {
          groupedMessages.push(currentGroup);
        }
        currentGroup = { userMessage: item, agentItems: [], agentLoading: false };
      } else if (item.loading) {
        // Loading placeholder
        if (currentGroup) {
          currentGroup.agentLoading = true;
        }
      } else if (currentGroup) {
        // Add agent item to current group (item already has role field)
        currentGroup.agentItems.push(item);
      }
    });

    // Add the last group
    if (currentGroup) {
      groupedMessages.push(currentGroup);
    }
  }

  return (
    <StyledFlex vertical>
      <Flex gap={16}>
        <ContextColumn vertical gap={4}>
          <SectionLabel>
            {survey.data.upload_type === SurveyUploadType.SURVEY
              ? 'Active survey'
              : 'Active project'}
          </SectionLabel>
          <StyledTag color="blue">{survey.data.name}</StyledTag>
        </ContextColumn>

        <ContextColumn vertical gap={4}>
          <Flex justify="space-between" align="center">
            <SectionLabel>
              {survey.data.upload_type === SurveyUploadType.SURVEY
                ? 'Additional surveys (optional)'
                : 'Additional projects (optional)'}
            </SectionLabel>
            {hasMessages && (
              <Button
                onClick={handleEndSession}
                danger
                icon={<CloseOutlined />}
                data-cy="end-session-btn"
                disabled={endingSession || getSurveyIdsLoading || updateSurveyIdsLoading}
                loading={endingSession}
                size="small"
              >
                End Session
              </Button>
            )}
          </Flex>
          <Select
            mode="multiple"
            showSearch
            placeholder="Select additional surveys..."
            options={surveys.data
              .filter((s) => s.id !== survey.data.id)
              .map((s) => ({
                label: s.name,
                value: s.id,
              }))}
            value={additionalSurveyIds}
            onChange={handleUpdateSurveyIds}
            loading={updateSurveyIdsLoading}
            disabled={
              sendMessageLoading ||
              getSurveyIdsLoading ||
              updateSurveyIdsLoading ||
              isProcessingMessage
            }
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </ContextColumn>
      </Flex>

      <StyledDivider />

      {!hasMessages ? (
        <WelcomePage
          inputValue={inputValue}
          setInputValue={setInputValue}
          sendMessage={sendMessage}
          loading={
            sendMessageLoading ||
            getSurveyIdsLoading ||
            updateSurveyIdsLoading ||
            isProcessingMessage
          }
        />
      ) : (
        <>
          <StyledMessageFlex vertical ref={setMessageContainerRef} gap={8}>
            {groupedMessages.map((group, groupIndex) => (
              <React.Fragment key={`group-${group.userMessage.timestamp}`}>
                {/* Render user message */}
                <Flex justify="end">
                  <MessageCard size="small" data-cy="user-message">
                    <MarkdownWithAssetLinks>{group.userMessage.text}</MarkdownWithAssetLinks>
                  </MessageCard>
                </Flex>
                {/* Render combined agent response with all items */}
                {(group.agentItems.length > 0 || group.agentLoading) && (
                  <MessageItem
                    key={`agent-${group.userMessage.timestamp}`}
                    items={group.agentItems}
                    loading={group.agentLoading}
                  />
                )}
                {/* Feedback buttons - show only when agent is done responding */}
                {group.agentItems.length > 0 && !group.agentLoading && (
                  <FeedbackButtons
                    messageIndex={groupIndex}
                    surveyId={surveyId}
                    initialRating={sessionFeedback?.data?.[groupIndex]}
                  />
                )}
              </React.Fragment>
            ))}
          </StyledMessageFlex>
          {!isUserAtBottom && (
            <ButtonContainer justify="center">
              <Button
                type="primary"
                shape="circle"
                icon={<DownOutlined />}
                onClick={scrollToLatestMessage}
                aria-label="Scroll to latest message"
              />
            </ButtonContainer>
          )}
          <BottomSection vertical gap={12} align="center">
            <MessageInput
              inputValue={inputValue}
              setInputValue={setInputValue}
              sendMessage={sendMessage}
              hasMessages
              loading={
                sendMessageLoading ||
                getSurveyIdsLoading ||
                updateSurveyIdsLoading ||
                isProcessingMessage
              }
              scrollToBottom={scrollToBottom}
            />
            <DisclaimerText type="secondary">
              AI-generated responses can contain inaccuracies, and you can verify important
              information in Theme Explorer or exported data.
            </DisclaimerText>
          </BottomSection>
        </>
      )}
    </StyledFlex>
  );
}

ChatTab.propTypes = {
  survey: queryType.isRequired,
};

export default ChatTab;
